import { Modal } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { deletePayment } from "../../../api/studentPaiement";
import { AxiosError } from "axios";
import StudentBourse from "../bourse_remise/studentBourse";
import AddPaymentModal from "./AddPaymentModal";
import EditPaymentModal from "./EditPaymentModal";
import PaymentDetailsModal from "./PaymentDetailsModal";
import StudentPaymentPlansTable from "./StudentPaymentPlansTable";
import StudentPaymentsTable from "./StudentPaymentsTable";
import FinancialReportModalApercu from "./FinancialReportModalApercu";
import {
  Action,
  buildPermission,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import withPermission from "../../../core/common/autorisations/withPermission";

type Props = {
  show: boolean;
  student?: any;
  onClose: () => void;
  onSave?: () => void;
  currency?: string;
};

const InfoStudentPaye = ({
  show,
  student,
  onClose,
  onSave,
  currency = "FCFA",
}: Props) => {
  const [checkedPlans, setCheckedPlans] = useState<string[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<any>(null);
  const [activeSection, setActiveSection] = useState<
    "plans" | "payments" | "bourses"
  >("plans");
  const [paymentFilter, setPaymentFilter] = useState<string>("Tous");

  const [showFinancialReport, setShowFinancialReport] = useState(false);

  const plans = useMemo(() => {
    const myPlans: any[] = Array.isArray(student?.plan) ? student?.plan : [];
    const myPayments: any[] = Array.isArray(student?.payments)
      ? student?.payments
      : [];
    const myPaymentRepartitions: any[] = myPayments
      .map((payment) =>
        Array.isArray(payment?.repartitions) ? payment?.repartitions : [],
      )
      .flat();

    return myPlans.map((plan) => ({
      ...plan,
      paid: myPaymentRepartitions.reduce(
        (s, r) => s + (r.payment_plan?.id === plan.id ? r.amount : 0),
        0,
      ),
    }));
  }, [student]);

  const receipts: any[] = useMemo(() => {
    const myPayments: any[] = Array.isArray(student?.payments)
      ? student?.payments
      : [];
    return myPayments.filter((payment) => !payment?.summaryOnly);
  }, [student]);

  useEffect(() => {
    if (show) {
      setActiveSection("plans");
      setPaymentFilter("Tous");
      setCheckedPlans([]);
    }
  }, [show, student?.id]);

  const MySwal = withReactContent(Swal);

  const showConfirmationAlert = (payment: any) => {
    MySwal.fire({
      title: "Supprimer un Paiement ?",
      text: "Souhaitez-vous vraiment supprimer ce paiement ?",
      showCancelButton: true,
      confirmButtonText: "Envoyer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      // ── Affiche le spinner pendant la requête ──
      MySwal.fire({
        title: "Suppression en cours...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => MySwal.showLoading(),
      });
      try {
        await deletePayment(payment.payment_id);
        MySwal.fire({
          title: "Paiement supprimée avec succès !",
          icon: "success",
          confirmButtonText: "OK",
        });
        if (onSave) onSave();
      } catch (error: any) {
        const err = error as AxiosError<any>;
        MySwal.fire({
          title: "Erreur",
          text:
            err.response?.data?.message ||
            err.response?.data?.error ||
            "Une erreur est survenue, réessayez plus tard",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    });
  };

  return (
    <>
      <Modal
        show={show && !showAdd && !showEdit && !showDetails}
        onHide={onClose}
        centered
        size="xl"
      >
        <Modal.Header className="border-bottom">
          <Modal.Title className="w-100">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex flex-column">
                <h5 className="mb-1">
                  {student?.utilisateur} ({student?.classe})
                </h5>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => setShowFinancialReport(true)}
                >
                  Imprimer
                </button>
                <button className="btn btn-danger" onClick={onClose}>
                  Fermer
                </button>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          <div className="row">
            <div className="col-md-2">
              <div className="list-group">
                <button
                  className={`list-group-item list-group-item-action ${
                    activeSection === "plans" ? "active" : ""
                  }`}
                  onClick={() => setActiveSection("plans")}
                >
                  Plans de paiement
                </button>
                <button
                  className={`list-group-item list-group-item-action ${
                    activeSection === "payments" ? "active" : ""
                  }`}
                  onClick={() => setActiveSection("payments")}
                >
                  Paiements
                </button>
                <button
                  className={`list-group-item list-group-item-action ${
                    activeSection === "bourses" ? "active" : ""
                  }`}
                  onClick={() => setActiveSection("bourses")}
                >
                  Bourses et Remises
                </button>
              </div>
            </div>

            <div className="col-md-10">
              {activeSection === "plans" && (
                <StudentPaymentPlansTable
                  student={student}
                  plans={plans}
                  checkedPlans={checkedPlans}
                  setCheckedPlans={setCheckedPlans}
                  onAddPayment={() => setShowAdd(true)}
                  currency={currency}
                />
              )}

              {activeSection === "payments" && (
                <StudentPaymentsTable
                  plans={plans}
                  receipts={receipts}
                  paymentFilter={paymentFilter}
                  setPaymentFilter={setPaymentFilter}
                  onView={(payment: any) => {
                    setSelectedPayment(payment);
                    setShowDetails(true);
                  }}
                  onEdit={(payment: any) => {
                    setSelectedPayment(payment);
                    setShowEdit(true);
                  }}
                  onDelete={(payment: any) => showConfirmationAlert(payment)}
                  // currency={currency}
                />
              )}

              {activeSection === "bourses" && (
                <StudentBourse
                  student={student}
                  onSaved={() => {
                    if (onSave) onSave();
                  }}
                  currency={currency}
                />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <AddPaymentModal
        show={showAdd}
        id_studentProfil={student?.id}
        onClose={() => setShowAdd(false)}
        selectedPlanIds={checkedPlans}
        allPlans={plans}
        onSave={() => {
          if (onSave) onSave();
        }}
      />

      <EditPaymentModal
        show={showEdit}
        payment={selectedPayment}
        id_studentProfil={student?.id}
        onClose={() => {
          setShowEdit(false);
          setSelectedPayment(null);
        }}
        selectedPlanIds={checkedPlans}
        allPlans={plans}
        onSave={() => {
          if (onSave) onSave();
        }}
      />

      <PaymentDetailsModal
        show={showDetails}
        payment={selectedPayment}
        onClose={() => {
          setShowDetails(false);
          setSelectedPayment(null);
        }}
        currency={currency}
      />

      <FinancialReportModalApercu
        show={showFinancialReport}
        metaData={student}
        onClose={() => setShowFinancialReport(false)}
      />
    </>
  );
};

export default withPermission(
  InfoStudentPaye,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT, action),
    ),
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.STUDENT_PAYMENT_PLAN, action),
    ),
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.BOURSE_REMISE, action),
    ),
  ],
  "masquer",
);
